import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatError, fileUrl } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import BookCard from "../../components/BookCard";
import { Plus, UploadCloud, X, BookOpen, Sparkles, Loader2 } from "lucide-react";

export default function MyBooks() {
    const { refreshUser } = useAuth();
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/books/mine");
            setBooks(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const onPosted = async () => {
        setShowForm(false);
        toast.success("Book posted!", { description: "You earned 100 Zapp coins." });
        await Promise.all([load(), refreshUser()]);
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-10">
            <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <div className="label-mini">My library</div>
                    <h1 className="font-serif text-4xl font-semibold">Books you’re lending</h1>
                    <p className="text-ink/60 mt-2">Post a book to earn <span className="font-bold text-zapp-dark">+100 Zapp coins</span>. You can lend it out anytime.</p>
                </div>
                <button data-testid="mybooks-add-btn" onClick={() => setShowForm(true)} className="btn-primary">
                    <Plus className="h-4 w-4" /> Post a book
                </button>
            </div>

            {showForm && <PostBookForm onClose={() => setShowForm(false)} onPosted={onPosted} />}

            {loading ? (
                <div className="text-ink/50">Loading…</div>
            ) : books.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-parchment rounded-2xl p-12 text-center" data-testid="mybooks-empty">
                    <div className="h-14 w-14 mx-auto rounded-full bg-paper flex items-center justify-center mb-4">
                        <BookOpen className="h-6 w-6 text-ink/40" />
                    </div>
                    <div className="font-serif text-xl mb-1">No books yet</div>
                    <p className="text-sm text-ink/60 mb-4">Add your first book — you&apos;ll earn 100 Zapp coins instantly.</p>
                    <button onClick={() => setShowForm(true)} className="btn-primary">
                        <Plus className="h-4 w-4" /> Post a book
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {books.map((b) => (
                        <BookCard key={b.id} book={b} />
                    ))}
                </div>
            )}
        </div>
    );
}

function PostBookForm({ onClose, onPosted }) {
    const [title, setTitle] = useState("");
    const [author, setAuthor] = useState("");
    const [price, setPrice] = useState("");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Auto-lookup state
    const [lookupBusy, setLookupBusy] = useState(false);
    const [suggestion, setSuggestion] = useState(null); // { title, author, cover_file_id }
    const [appliedTitle, setAppliedTitle] = useState(null); // don't re-fetch same title
    const [autoCoverFileId, setAutoCoverFileId] = useState(null); // cover fetched from lookup
    const debounceRef = useRef(null);

    const pickFile = (f) => {
        setFile(f);
        setAutoCoverFileId(null); // manual upload overrides auto
        if (f) setPreview(URL.createObjectURL(f));
    };

    // Debounced title lookup
    useEffect(() => {
        const t = title.trim();
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (t.length < 3 || t === appliedTitle) {
            setSuggestion(null);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setLookupBusy(true);
            try {
                const { data } = await api.get(`/books/lookup`, { params: { title: t } });
                if (data && data.title) {
                    setSuggestion(data);
                } else {
                    setSuggestion({ notFound: true });
                }
            } catch {
                setSuggestion(null);
            } finally {
                setLookupBusy(false);
            }
        }, 700);
        return () => clearTimeout(debounceRef.current);
    }, [title, appliedTitle]);

    const applySuggestion = () => {
        if (!suggestion || suggestion.notFound) return;
        if (suggestion.author) setAuthor(suggestion.author);
        if (suggestion.cover_file_id) {
            setAutoCoverFileId(suggestion.cover_file_id);
            setPreview(fileUrl(suggestion.cover_file_id));
            setFile(null);
        }
        setAppliedTitle(title.trim());
        setSuggestion(null);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file && !autoCoverFileId) { toast.error("Please upload or auto-fetch a cover image"); return; }
        if (Number(price) < 0) { toast.error("Price must be zero or more"); return; }
        setUploading(true);
        try {
            let cover_file_id = autoCoverFileId;
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                cover_file_id = up.data.file_id;
            }
            setUploading(false); setSaving(true);
            const bookFd = new FormData();
            bookFd.append("title", title);
            bookFd.append("author", author);
            bookFd.append("price_per_day", String(Number(price) || 0));
            bookFd.append("cover_file_id", cover_file_id);
            await api.post("/books", bookFd, { headers: { "Content-Type": "multipart/form-data" } });
            onPosted();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setUploading(false); setSaving(false);
        }
    };

    return (
        <div className="bg-white border border-parchment rounded-2xl p-6 mb-8 shadow-sm relative">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-paper" data-testid="postbook-close">
                <X className="h-4 w-4" />
            </button>
            <h2 className="font-serif text-2xl font-semibold mb-4">Post a book to lend</h2>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-6">
                <div>
                    <label className="label-mini">Cover image</label>
                    <label className="w-full border-2 border-dashed border-parchment rounded-xl p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-paper transition-colors" data-testid="postbook-file-label">
                        {preview ? (
                            <img src={preview} alt="preview" className="max-h-56 rounded-md object-cover" />
                        ) : (
                            <>
                                <UploadCloud className="h-8 w-8 text-ink/40 mb-2" />
                                <div className="text-sm font-medium">Click to upload cover</div>
                                <div className="text-xs text-ink/50 mt-1">PNG or JPG · up to 5MB</div>
                                <div className="text-xs text-library mt-2">or type a title above &mdash; we&apos;ll fetch it for you</div>
                            </>
                        )}
                        <input data-testid="postbook-file-input" type="file" accept="image/*" onChange={(e) => pickFile(e.target.files[0])} className="hidden" />
                    </label>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="label-mini flex items-center justify-between">
                            <span>Book title</span>
                            {lookupBusy && (
                                <span className="text-[10px] normal-case tracking-normal text-ink/50 inline-flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Looking up…
                                </span>
                            )}
                        </label>
                        <input data-testid="postbook-title-input" required value={title} onChange={(e) => setTitle(e.target.value)} className="input-field" placeholder="Atomic Habits" />
                        {suggestion && !suggestion.notFound && (
                            <div className="mt-2 flex items-center gap-3 p-3 rounded-xl bg-library/5 border border-library/20" data-testid="postbook-suggestion">
                                {suggestion.cover_file_id ? (
                                    <img src={fileUrl(suggestion.cover_file_id)} alt="" className="h-14 w-10 object-cover rounded border border-parchment" />
                                ) : (
                                    <div className="h-14 w-10 rounded border border-parchment bg-white flex items-center justify-center text-ink/30">
                                        <BookOpen className="h-4 w-4" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs uppercase tracking-widest text-library font-bold flex items-center gap-1">
                                        <Sparkles className="h-3 w-3" /> Found on Open Library
                                    </div>
                                    <div className="text-sm font-semibold truncate">{suggestion.title}</div>
                                    <div className="text-xs text-ink/60 truncate">by {suggestion.author}</div>
                                </div>
                                <button type="button" onClick={applySuggestion} data-testid="postbook-suggestion-apply" className="btn-accept text-xs px-2.5 py-1 flex-shrink-0">
                                    Use this
                                </button>
                            </div>
                        )}
                        {suggestion?.notFound && (
                            <div className="mt-2 text-xs text-ink/50" data-testid="postbook-suggestion-none">
                                No match found — fill the details manually.
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="label-mini">Author</label>
                        <input data-testid="postbook-author-input" required value={author} onChange={(e) => setAuthor(e.target.value)} className="input-field" placeholder="James Clear" />
                    </div>
                    <div>
                        <label className="label-mini">Price per day (Zapp coins)</label>
                        <input data-testid="postbook-price-input" type="number" min={0} step={1} required value={price} onChange={(e) => setPrice(e.target.value)} className="input-field" placeholder="5" />
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                        <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
                        <button data-testid="postbook-submit-btn" type="submit" disabled={uploading || saving} className="btn-primary">
                            {uploading ? "Uploading…" : saving ? "Posting…" : "Post & earn 100 coins"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
